import {useIntl} from 'react-intl';

import {solvedHtmlMessage} from '@/libs/intlHelper';

function useIntlFormat() {
  const {formatMessage} = useIntl();

  /**
   * Formats an HTML message using the provided message descriptor, values, and options. This function is an expansion of the 'formatMessage' function provided by react-intl, specifically designed for handling HTML messages.
   * @param {object} messageDescriptor - The message descriptor object containing the properties 'id', 'description', and 'defaultMessage' as required by react-intl.
   * @param {object} [value=null] - An optional object containing values to be formatted into the message. Each key in this object corresponds to a placeholder in the message.
   * @param {object} [opt=null] - An optional object containing additional formatting options. This object can include any options supported by react-intl's 'formatMessage' function.
   * @returns {string} The formatted HTML message.
   * @see {@link https://formatjs.io/docs/getting-started/installation} for more information on react-intl and its formatMessage function.
   * @example
   * // Simple usage:
   * const messageDescriptor = {
   *   id: 'app.greeting',
   *   description: 'Greeting message',
   *   defaultMessage: 'Hello, {name}!'
   * };
   * const formattedMessage = formatHTMLMessage(messageDescriptor, { name: 'World' });
   * // Result: 'Hello, World!'
   *
   * // With additional options:
   * const opt = { localeMatcher: 'best fit' };
   * const formattedMessage = formatHTMLMessage(messageDescriptor, { name: 'World' }, opt);
   * // Result: 'Hello, World!' (formatted according to the localeMatcher option)
   *
   * // using tag html a (<a/>): please add _href in the parameter value
   * const opt = { localeMatcher: 'best fit' };
   * const formattedMessage = formatHTMLMessage(messageDescriptor, { name: 'World', _href: 'link' });
   * // Result: '<a href={"link"}>Hello, World!</a>'
   *
   * // Unsupported braces value on chromium v72 (uPulse Device v2)
   * Add replaceValueMessage(message, value) to supporting intl usage on chromium v72
   */
  const formatHTMLMessage = ({id, description, defaultMessage}, value = {}, opt = null) => {
    const messageDescriptor = {};
    if (id) messageDescriptor.id = id;
    if (description) messageDescriptor.description = description;
    if (defaultMessage) messageDescriptor.defaultMessage = defaultMessage;

    return Object.keys(value).length === 0
      ? formatMessage(
          messageDescriptor,
          {
            ...solvedHtmlMessage({...value}),
          },
          opt,
        )
      : replaceValueMessage(
          formatMessage(
            messageDescriptor,
            {
              ...solvedHtmlMessage({...value}),
            },
            opt,
          ),
          value,
        );
  };

  const replaceValueMessage = (message, value) => {
    const replacements = replaceKeysWithBraces(value);

    const pattern = new RegExp(Object.keys(replacements).join('|'), 'g');
    return message.replace(pattern, (matched) => replacements[matched]);
  };

  const replaceKeysWithBraces = (jsonObj) =>
    Object.keys(jsonObj).reduce((result, key) => {
      result[`{${key}}`] = jsonObj[key];
      return result;
    }, {});

  return {formatHTMLMessage, formatMessage};
}
export default useIntlFormat;
